import React, { useCallback, useEffect, useRef } from 'react';
import { DrawCanvas } from './styles';
import { DrawingStroke, DrawPoint, DrawTool, roundCoord } from './drawing';

const STROKE_COLOR = '#101010';
export const PEN_SIZE = 3;
export const ERASER_SIZE = 22;
// Janela em que toques são ignorados após o último evento de caneta (palm rejection);
// expirada a janela, o aluno volta a poder desenhar com o dedo
export const PALM_REJECTION_WINDOW_MS = 3000;

interface ActiveStroke {
  stroke: DrawingStroke;
  pointerId: number;
  pointerType: string;
  // Último ponto em px CSS, para desenhar segmentos incrementais durante o traço
  lastPoint: DrawPoint;
}

interface DrawingCanvasProps {
  strokes: DrawingStroke[];
  tool: DrawTool;
  visible: boolean;
  onCommitStroke: (stroke: DrawingStroke) => void;
}

type StrokeStyleContext = Pick<
  CanvasRenderingContext2D,
  | 'globalCompositeOperation'
  | 'strokeStyle'
  | 'fillStyle'
  | 'lineWidth'
  | 'lineCap'
  | 'lineJoin'
>;

// A borracha usa destination-out: apaga o que já foi pintado preservando o fundo transparente
const applyStrokeStyle = (ctx: StrokeStyleContext, stroke: DrawingStroke) => {
  ctx.globalCompositeOperation = stroke.tool === 'eraser' ? 'destination-out' : 'source-over';
  ctx.strokeStyle = STROKE_COLOR;
  ctx.fillStyle = STROKE_COLOR;
  ctx.lineWidth = stroke.size;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
};

const drawDot = (ctx: CanvasRenderingContext2D, point: DrawPoint, stroke: DrawingStroke) => {
  applyStrokeStyle(ctx, stroke);
  ctx.beginPath();
  ctx.arc(point.x, point.y, stroke.size / 2, 0, Math.PI * 2);
  ctx.fill();
};

const drawSegment = (
  ctx: CanvasRenderingContext2D,
  from: DrawPoint,
  to: DrawPoint,
  stroke: DrawingStroke,
) => {
  applyStrokeStyle(ctx, stroke);
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();
};

export function DrawingCanvas({ strokes, tool, visible, onCommitStroke }: DrawingCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const activeStrokeRef = useRef<ActiveStroke | null>(null);
  // Palm rejection: timestamp do último evento de caneta; toques são ignorados só dentro
  // da janela recente (guardou a caneta, o dedo volta a desenhar)
  const lastPenAtRef = useRef(0);
  const strokesRef = useRef(strokes);

  const getContext = () => canvasRef.current?.getContext('2d') ?? null;

  const getCssSize = () => {
    const rect = canvasRef.current?.getBoundingClientRect();
    return { width: Math.max(rect?.width ?? 0, 1), height: Math.max(rect?.height ?? 0, 1) };
  };

  // Converte o evento para px CSS relativos ao canvas e para coordenadas normalizadas (0..1)
  const toPoint = (event: { clientX: number; clientY: number }) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    const { width, height } = getCssSize();
    const px = { x: event.clientX - (rect?.left ?? 0), y: event.clientY - (rect?.top ?? 0) };
    return { px, norm: { x: roundCoord(px.x / width), y: roundCoord(px.y / height) } };
  };

  const redrawAll = useCallback((allStrokes: DrawingStroke[]) => {
    const canvas = canvasRef.current;
    const ctx = getContext();
    if (!canvas || !ctx) return;

    const { width, height } = getCssSize();
    ctx.clearRect(0, 0, Math.max(width, canvas.width), Math.max(height, canvas.height));

    for (const stroke of allStrokes) {
      const points = stroke.points.map((p) => ({ x: p.x * width, y: p.y * height }));
      if (points.length === 0) continue;
      if (points.length === 1) {
        drawDot(ctx, points[0], stroke);
        continue;
      }
      applyStrokeStyle(ctx, stroke);
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.stroke();
    }
  }, []);

  // Ajusta o backing store ao tamanho CSS x devicePixelRatio (traço nítido em telas retina)
  // e redesenha; roda ao abrir o modo desenho e a cada redimensionamento/rotação da tela.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!visible || !canvas) return undefined;

    const syncSize = () => {
      const ctx = getContext();
      if (!ctx) return;
      const { width, height } = getCssSize();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(Math.round(width * dpr), 1);
      canvas.height = Math.max(Math.round(height * dpr), 1);
      if (typeof ctx.setTransform === 'function') ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      redrawAll(strokesRef.current);
    };

    syncSize();

    // Agrupa por quadro (rAF): durante o arraste do painel o ResizeObserver dispara por
    // frame, e cada syncSize limpa o bitmap e repinta todos os traços
    let rafId = 0;
    const scheduleSync = () => {
      if (typeof requestAnimationFrame !== 'function') {
        syncSize();
        return;
      }
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        syncSize();
      });
    };

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(scheduleSync);
      observer.observe(canvas);
    }
    window.addEventListener('resize', scheduleSync);
    window.addEventListener('orientationchange', scheduleSync);
    return () => {
      if (rafId && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(rafId);
      observer?.disconnect();
      window.removeEventListener('resize', scheduleSync);
      window.removeEventListener('orientationchange', scheduleSync);
    };
  }, [visible, redrawAll]);

  useEffect(() => {
    strokesRef.current = strokes;
    if (visible) redrawAll(strokes);
  }, [strokes, visible, redrawAll]);

  // Descarta o traço em andamento sem confirmá-lo (ex.: palma encostou antes da caneta)
  const abortActiveStroke = () => {
    activeStrokeRef.current = null;
    redrawAll(strokesRef.current);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = getContext();
    if (!ctx) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    if (e.pointerType === 'pen') {
      lastPenAtRef.current = Date.now();
      // A palma costuma tocar a tela antes da ponta da caneta: um traço de toque em
      // andamento é descartado em favor do traço da caneta
      if (activeStrokeRef.current?.pointerType === 'touch') abortActiveStroke();
    }
    if (e.pointerType === 'touch' && Date.now() - lastPenAtRef.current < PALM_REJECTION_WINDOW_MS)
      return;
    // Um traço por vez: ignora um segundo dedo/ponteiro simultâneo
    if (activeStrokeRef.current) return;

    e.preventDefault();
    const { px, norm } = toPoint(e);
    const stroke: DrawingStroke = {
      tool,
      size: tool === 'eraser' ? ERASER_SIZE : PEN_SIZE,
      points: [norm],
    };
    activeStrokeRef.current = {
      stroke,
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      lastPoint: px,
    };
    drawDot(ctx, px, stroke);

    try {
      // Captura o ponteiro para o traço continuar mesmo se sair dos limites do canvas
      canvasRef.current?.setPointerCapture?.(e.pointerId);
    } catch {
      // ambientes sem pointer capture (jsdom/browsers antigos): o desenho segue funcionando
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const active = activeStrokeRef.current;
    const ctx = getContext();
    if (!active || !ctx || e.pointerId !== active.pointerId) return;

    e.preventDefault();
    if (e.pointerType === 'pen') lastPenAtRef.current = Date.now();
    // getCoalescedEvents entrega os pontos intermediários agrupados pelo navegador
    const native = e.nativeEvent as PointerEvent;
    const coalesced =
      typeof native.getCoalescedEvents === 'function' ? native.getCoalescedEvents() : [];
    const events: Array<{ clientX: number; clientY: number }> =
      coalesced.length > 0 ? coalesced : [native];

    for (const event of events) {
      const { px, norm } = toPoint(event);
      active.stroke.points.push(norm);
      drawSegment(ctx, active.lastPoint, px, active.stroke);
      active.lastPoint = px;
    }
  };

  const releasePointer = (pointerId: number) => {
    try {
      canvasRef.current?.releasePointerCapture?.(pointerId);
    } catch {
      // ponteiro já liberado ou sem suporte a capture: nada a fazer
    }
  };

  const finishStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const active = activeStrokeRef.current;
    if (!active || e.pointerId !== active.pointerId) return;

    activeStrokeRef.current = null;
    releasePointer(e.pointerId);
    onCommitStroke(active.stroke);
  };

  // pointercancel = o navegador tomou o ponteiro (gesto do sistema, chamada, palma):
  // o traço parcial é descartado em vez de entrar no histórico
  const cancelStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const active = activeStrokeRef.current;
    if (!active || e.pointerId !== active.pointerId) return;

    releasePointer(e.pointerId);
    abortActiveStroke();
  };

  return (
    <DrawCanvas
      ref={canvasRef}
      data-testid="notesdrawer-canvas"
      aria-label="Área de desenho das anotações"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishStroke}
      onPointerCancel={cancelStroke}
      onPointerLeave={finishStroke}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
}
